import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { View } from 'react-native';
import Toast from 'react-native-toast-message';
import { UnistylesRuntime, useStyles } from 'react-native-unistyles';

import type { Reservation } from '@/api';
import type { Car } from '@/api/cars/types';
import { useCarCalendarReservations } from '@/api/partners/cars/use-car-calendar';
import { BlockCarModal } from '@/components/reservations/block-car-modal';
import { DeleteBlockModal } from '@/components/reservations/delete-block-modal';
import {
  getPartnerReservationStatusColor,
  getPartnerReservationStatusLabel,
} from '@/components/reservations/status-label';
import { capitalizeFirstLetter } from '@/core';
import { Button } from '@/ui';
import type {
  EventcalendarBase,
  MbscCalendarEvent,
  MbscEventcalendarView,
  MbscEventClickEvent,
  MbscEventCreatedEvent,
  MbscEventCreateFailedEvent,
  MbscPageLoadedEvent,
} from '@/ui/event-calendar';
import {
  CalendarNav,
  CalendarNext,
  CalendarPrev,
  CalendarToday,
  Eventcalendar,
} from '@/ui/event-calendar';
import { Icon } from '@/ui/icon';
import { ChevronRight } from '@/ui/icons/chevron-right';

interface RentalCalendarProps {
  car: Car;
}

export default function RentalCalendar({ car }: RentalCalendarProps) {
  const { theme } = useStyles();
  const [dateType, setDateType] = useState<'month' | 'week'>('month');
  const [firstDay, setFirstDay] = useState<Date | null>(null);
  const [lastDay, setLastDay] = useState<Date | null>(null);
  const [showBlockCarModal, setShowBlockCarModal] = useState(false);
  const [showDeleteBlockModal, setShowDeleteBlockModal] = useState(false);
  const [activeTrip, setActiveTrip] = useState<Reservation | null>(null);
  const [newBlock, setNewBlock] = useState<{
    startDate: Date;
    endDate: Date;
    carId: string;
  } | null>(null);

  const { data: calendarTrips, refetch: refetchCalendarTrips } =
    useCarCalendarReservations({
      variables: {
        id: car.id,
        startDatetime: firstDay?.toISOString(),
        endDatetime: lastDay?.toISOString(),
      },
      enabled: !!firstDay && !!lastDay,
    });

  const view = useMemo((): MbscEventcalendarView => {
    return {
      calendar: {
        type: dateType,
      },
    };
  }, [dateType]);

  const events = useMemo((): MbscCalendarEvent[] => {
    if (!calendarTrips) return [];

    const userZone = DateTime.local().zoneName;

    return calendarTrips.map((res: Reservation) => {
      const startDT = DateTime.fromISO(res.startTime, {
        setZone: true,
      }).setZone(userZone, { keepLocalTime: true });
      const endDT = DateTime.fromISO(res.endTime, { setZone: true }).setZone(
        userZone,
        { keepLocalTime: true }
      );
      const startTime = startDT.toFormat('hh:mm a');
      const endTime = endDT.toFormat('hh:mm a');
      const tz = DateTime.fromISO(res.startTime, { setZone: true }).setZone(
        res.car.defaultLocation.timezone,
        { keepLocalTime: true }
      ).offsetNameShort;
      const mappedStatusLabel = getPartnerReservationStatusLabel(
        res.status,
        res.isBlock
      );

      return {
        id: res.id,
        start: DateTime.fromISO(res.startTime, { setZone: true })
          .setZone(userZone, { keepLocalTime: true })
          .toISO(),
        end: DateTime.fromISO(res.endTime, { setZone: true })
          .setZone(userZone, { keepLocalTime: true })
          .toISO(),
        title: `${res.customerName} #${res.id} ${startTime} - ${endTime} ${tz}`,
        color: getPartnerReservationStatusColor(res.status, res.isBlock),
        tooltip: `
          Current status: ${mappedStatusLabel}
          Rented for ${res.numDays} days
          Pickup Location: ${
            res.customPickupLocation || res.pickupLocation.address
          }
          Dropoff Location: ${
            res.customReturnLocation || res.returnLocation.address
          }
        `,
      };
    });
  }, [calendarTrips, dateType]);

  const handleCalendarLoad = (
    args: MbscPageLoadedEvent,
    inst: EventcalendarBase
  ) => {
    setFirstDay(args.firstDay);
    setLastDay(args.lastDay);
  };

  const handleEventClick = (
    args: MbscEventClickEvent,
    inst: EventcalendarBase
  ) => {
    const resId = args.event.id;
    const trip = calendarTrips.find((res: Reservation) => res.id === resId);

    if (trip?.isBlock) {
      setActiveTrip(trip);
      setShowDeleteBlockModal(true);
      return;
    }

    window.open(`/partner/reservations/${args.event.id}`, '_blank');
  };

  const handleEventCreate = (
    args: MbscEventCreatedEvent,
    inst: EventcalendarBase
  ) => {
    setShowBlockCarModal(true);

    setNewBlock({
      startDate: new Date(args.event.start),
      endDate: new Date(args.event.end),
      carId: car.id.toString(),
    });

    setTimeout(() => {
      inst.setEvents(events);
    }, 500);
  };

  const handleEventCreateFailed = (
    args: MbscEventCreateFailedEvent,
    inst: EventcalendarBase
  ) => {
    if (args.invalid) {
      Toast.show({
        type: 'error',
        text1: 'An error occured.',
      });
    } else if (args.overlap) {
      Toast.show({
        type: 'error',
        text1: 'This car is already rented for this date.',
      });
    }
  };

  return (
    <View>
      <Eventcalendar
        theme="ios"
        themeVariant={UnistylesRuntime.themeName}
        view={view}
        data={events}
        width="100%"
        dragToCreate
        onEventCreated={handleEventCreate}
        onEventCreateFailed={handleEventCreateFailed}
        onPageLoaded={handleCalendarLoad}
        onEventDoubleClick={handleEventClick}
        eventOverlap={false}
        renderHeader={() => (
          <>
            <View>
              <View style={{ flexDirection: 'row' }}>
                <CalendarNav className="mds-header-filter-nav" />

                <Button
                  type="secondaryExtraSmall"
                  text={capitalizeFirstLetter(dateType)}
                  onPress={() => {
                    setDateType(dateType === 'month' ? 'week' : 'month');
                  }}
                  style={{ width: 80, height: 40 }}
                  rightIcon={
                    <Icon
                      type="naked"
                      icon={<ChevronRight />}
                      fillColor={theme.colors.icon.color}
                      width={12}
                      height={12}
                      iconStyle={{
                        transform: [{ rotate: '90deg' }],
                      }}
                    />
                  }
                />
              </View>
            </View>
            <div className="mds-header-filter mbsc-flex-1-0" />
            <CalendarPrev
              className={
                UnistylesRuntime.themeName === 'dark'
                  ? 'mds-header-filter-prev-dark'
                  : 'mds-header-filter-prev'
              }
            />
            <CalendarToday
              className={
                UnistylesRuntime.themeName === 'dark'
                  ? 'mds-header-filter-today-dark'
                  : 'mds-header-filter-today'
              }
            />
            <CalendarNext
              className={
                UnistylesRuntime.themeName === 'dark'
                  ? 'mds-header-filter-next-dark'
                  : 'mds-header-filter-next'
              }
            />
          </>
        )}
      />

      {newBlock ? (
        <BlockCarModal
          visible={showBlockCarModal}
          startDate={newBlock.startDate}
          endDate={newBlock.endDate}
          car={car}
          onClose={() => {
            setShowBlockCarModal(false);
          }}
          onSuccess={() => {
            Toast.show({
              text1: 'Car blocked successfully',
              type: 'success',
            });
            setShowBlockCarModal(false);
            refetchCalendarTrips();
          }}
        />
      ) : null}

      <DeleteBlockModal
        visible={showDeleteBlockModal}
        trip={activeTrip}
        onClose={() => {
          setShowDeleteBlockModal(false);
        }}
        onSuccess={() => {
          Toast.show({
            text1: 'Block deleted successfully',
            type: 'success',
          });
          setShowDeleteBlockModal(false);
          refetchCalendarTrips();
        }}
      />
    </View>
  );
}
